import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiDataTable } from 'components/common'
import { Badge, Box, Button, Flex, Heading, Link, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useDisclosure } from '@chakra-ui/react'
import { deleteInstallationVendor, getInstallationVendors, exportInstallationVendors } from 'api'
import { Link as NavLink, useNavigate } from 'react-router-dom'
import { MdAdd } from 'react-icons/md'
import { useAppContext } from 'hooks/useAppContext'
import { getAssetUrl } from 'lib'
import { ModalNotes } from './ModalNotes'

const isExpired = (date) => {
  if (!date) return false
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return false
  return d.getTime() < Date.now()
}

const CoiCell = ({ coiPath, coiExpiresAt }) => {
  if (!coiPath) return <Text fontSize="sm" color="gray.500">-</Text>
  return (
    <Flex direction="column" gap={1} alignItems="flex-start">
      <Link href={getAssetUrl(coiPath)} isExternal color="blue.500" fontSize="sm">View</Link>
      {coiExpiresAt && (
        <Badge colorScheme={isExpired(coiExpiresAt) ? 'red' : 'green'}>
          {isExpired(coiExpiresAt) ? 'Expired' : 'Valid'} {new Date(coiExpiresAt).toLocaleDateString()}
        </Badge>
      )}
    </Flex>
  )
}

CoiCell.propTypes = {
  coiPath: PropTypes.string,
  coiExpiresAt: PropTypes.string
}

const BreadcrumbNav = [
  {
    label: 'Installation Vendors',
    isCurrentPage: true
  }
]

const INSTALLATION_VENDORS_COLUMNS = [
  {
    label: 'Name',
    orderBy: 'name',
    render: ({ name }) => name
  },
  {
    label: 'Contact',
    orderBy: 'contact',
    render: ({ contact }) => contact
  },
  {
    label: 'Email',
    orderBy: 'email',
    render: ({ email }) => email
  },
  {
    label: 'Phone',
    orderBy: 'phone',
    render: ({ phone }) => phone
  },
  {
    label: 'Address',
    orderBy: 'address',
    render: ({ address }) => address
  },
  {
    label: 'City',
    orderBy: 'city',
    render: ({ city }) => city
  },
  {
    label: 'State',
    orderBy: 'state',
    render: ({ state }) => state
  },
  {
    label: 'Zip',
    orderBy: 'zip',
    render: ({ zip }) => zip
  },
  {
    label: 'COI',
    render: ({ coiPath, coiExpiresAt }) => <CoiCell coiPath={coiPath} coiExpiresAt={coiExpiresAt} />
  },
  {
    label: 'W-9',
    render: ({ w9Path }) => (
      w9Path
        ? <Link href={getAssetUrl(w9Path)} isExternal color="blue.500" fontSize="sm">View</Link>
        : <Text fontSize="sm" color="gray.500">-</Text>
    )
  },
  {
    label: 'Actions',
    render: ({ notesCount, onRemove, onNotes, ...rest }) => (
      <Flex columnGap={2}>
        <Button size="xs" colorScheme="gray" onClick={() => onNotes(rest)}>{`Notes (${notesCount})`}</Button>
        <Button as={NavLink} to={`/system/installation-vendors/edit/${rest.id}`} size="xs" colorScheme="blue">Edit</Button>
        <Button size="xs" colorScheme="red" onClick={() => onRemove(rest)}>Remove</Button>
      </Flex>
    )
  }
]

export const InstallationVendorList = () => {
  const navigate = useNavigate()
  const disclosureRemove = useDisclosure()
  const disclosureNotes = useDisclosure()
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'name',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedVendor, setSelectedVendor] = useState(null)

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getInstallationVendors(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onRemove = (vendor) => {
    setSelectedVendor(vendor)
    disclosureRemove.onOpen()
  }

  const handleRemove = async (vendorId) => {
    setIsRemoving(true)
    try {
      await deleteInstallationVendor(vendorId)
      setIsRemoving(false)
      disclosureRemove.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsRemoving(false)
    }
  }

  const onNotes = (vendor) => {
    setSelectedVendor(vendor)
    disclosureNotes.onOpen()
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, onRemove, onNotes }))
  }, [rows])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Installation Vendors</Heading>
          <Button
            size="md"
            colorScheme="blue"
            onClick={() => navigate('/system/installation-vendors/add')}
            leftIcon={<MdAdd size="24px" color="white" />}
          >
            Add Vendor
          </Button>
        </Flex>
        <UiDataTable
          tableConfig={tableConfig}
          updateTableConfig={setTableConfig}
          rows={customizedRows}
          rowKey="id"
          columns={INSTALLATION_VENDORS_COLUMNS}
          isLoading={isLoading}
          exportConfig={{ filename: 'installation-vendors.csv', request: exportInstallationVendors }}
        />
      </Box>
      <ModalRemoveInstallationVendor
        vendor={selectedVendor}
        isRemoving={isRemoving}
        handleRemove={() => handleRemove(selectedVendor?.id)}
        {...disclosureRemove}
      />
      <ModalNotes
        vendor={selectedVendor}
        reload={load}
        {...disclosureNotes}
      />
    </>
  )
}

const ModalRemoveInstallationVendor = ({ vendor, isRemoving, handleRemove, isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remove Vendor</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>Are you sure to remove the vendor - {vendor?.name}?</Text>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='red' mr={3} onClick={handleRemove} isLoading={isRemoving}>
            Remove
          </Button>
          <Button variant='ghost' onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

ModalRemoveInstallationVendor.propTypes = {
  vendor: PropTypes.object,
  isRemoving: PropTypes.bool,
  handleRemove: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}
